"use client"

// Client component: when `optimize` is set we pass a `loader` FUNCTION to
// next/image (the Cloudflare resize loader). A function prop cannot cross the
// server→client boundary, so any Server Component using <CdnImage optimize>
// (homepage hero/category/tier/split/artisan blocks) would crash with
// "Functions cannot be passed directly to Client Components". Marking this
// wrapper "use client" creates the loader inside the client boundary, so the
// server parent only ever passes serializable props (src, alt, optimize, …).

import NextImage, { type ImageProps } from "next/image"
import { forwardRef, useCallback, useEffect, useRef, useState } from "react"
import { getConfiguredImageHosts } from "@lib/brand/branding"
import cloudflareImageLoader from "@lib/image/cloudflare-loader"

const DIRECT_CDN_IMAGE_HOSTS = new Set(getConfiguredImageHosts())

export function shouldBypassNextImageOptimizer(src: ImageProps["src"]): boolean {
  if (typeof src !== "string") {
    return false
  }

  try {
    const url = new URL(src)
    return url.protocol === "https:" && DIRECT_CDN_IMAGE_HOSTS.has(url.hostname)
  } catch {
    return false
  }
}

type CdnImageProps = ImageProps & {
  // Pass optimize to re-enable Next.js resizing + WebP conversion for this
  // image. Use on thumbnails and product cards (displayed at <50vw) where the
  // full original resolution is wasted. Keep unoptimized (the default) for
  // full-bleed heroes and PDP gallery images where quality matters most.
  optimize?: boolean
}

// forwardRef so callers (e.g. ImageWithLoader) can read the underlying <img>'s
// `complete`/`naturalWidth` to detect an image that painted from cache before a
// React onLoad handler attached.
const CdnImage = forwardRef<HTMLImageElement, CdnImageProps>(function CdnImage(
  { optimize, ...props },
  ref
) {
  const isCdnHost = shouldBypassNextImageOptimizer(props.src)

  // /cdn-cgi/image can refuse a transform for reasons that have nothing to do
  // with the asset — most importantly `429 / err=9422` once the account's
  // monthly unique-transformation allowance is spent. next/image has no notion
  // of that: it paints the 429's text/plain body as a broken <img>. Remember the
  // src that failed and re-render it straight from the CDN, unresized. Keyed by
  // src so a gallery swapping images retries the edge for the next one.
  const [failedSrc, setFailedSrc] = useState<ImageProps["src"] | null>(null)
  const edgeResizeFailed = failedSrc === props.src

  // Optimised CDN images go through Cloudflare Image Resizing (/cdn-cgi/image,
  // enabled on the cdn.alif.clothing zone): edge-cached + Polished AVIF served
  // direct from the CDN, with resizing done at the edge instead of on this
  // origin. Non-CDN optimised images fall back to the Next.js optimizer;
  // un-optimised CDN images (hero, PDP gallery) keep serving the raw original.
  const useCfResize = !!optimize && isCdnHost && !edgeResizeFailed

  const imgRef = useRef<HTMLImageElement | null>(null)
  const setRefs = useCallback(
    (node: HTMLImageElement | null) => {
      imgRef.current = node
      if (typeof ref === "function") {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
    },
    [ref]
  )

  // Error-race guard, twin of ImageWithLoader's cache-race guard: a server-
  // rendered <img> can finish its request BEFORE React hydrates and attaches
  // onError, so the error event is never delivered and the fallback below never
  // arms. A finished image with naturalWidth 0 is a failed image — the hero
  // banner (SSR'd, above the fold) fails exactly this way.
  useEffect(() => {
    if (!useCfResize) {
      return
    }
    const node = imgRef.current
    if (node?.complete && node.naturalWidth === 0) {
      setFailedSrc(props.src)
    }
  }, [useCfResize, props.src])

  return (
    <NextImage
      ref={setRefs}
      {...props}
      {...(useCfResize ? { loader: cloudflareImageLoader } : {})}
      unoptimized={
        useCfResize
          ? false
          : edgeResizeFailed
            ? true
            : (props.unoptimized ?? (!optimize && isCdnHost))
      }
      onError={(event) => {
        if (useCfResize) {
          // Recoverable: swap to the original and let it try again. Withhold the
          // caller's onError (ImageWithLoader clears its shimmer there) until we
          // know the fallback failed too.
          setFailedSrc(props.src)
          return
        }
        props.onError?.(event)
      }}
    />
  )
})

export default CdnImage
